import { BarChartItemData } from "common/DS/HorizontalProgressBarChart";

import {
    getEnpsColor,
    getEnpsValueClass,
    getFavorabilityBarChartColor,
    getFavorabilityColor,
    getFavorabilityValueClass,
    getResponsivenessColor,
    getResponsivenessValueClass,
} from "./colors";

const formatPercent = (number: number, decimals = 0) => {
    const percent = number * 100;
    // Don't show ".0"
    const formatted = percent.toFixed(decimals).replace(/\.[0]+$/, "");
    return `${formatted}%`;
};

const formatPerc = (val: number | null) => (val != null ? `${val}%` : "");

export const formatPercOrNa = (val: number | null) => formatPerc(val) || "N/A";

export const formatRatioPercent = (count: MaybeNumber, total: MaybeNumber, decimals = 1) =>
    (total || 0) > 0 ? formatPercent((count || 0) / (total || 1), decimals) : "0%";

const wholePercentNumRound = (num: number): number => Math.round((num || 0) * 100);
const wholePercentNumFloor = (num: number): number => Math.floor((num || 0) * 100);
export const wholePercentNum = <T extends number | null>(num: T | undefined, floor = false): T => {
    if (num == null) {
        return null as T;
    }
    const val = floor ? wholePercentNumFloor(num) : wholePercentNumRound(num);
    return val as T;
};

export function formatThemeItem(item: RelayFavorabilityItem): FavorabilityThemeItem {
    const favorablePerc = wholePercentNum(item.favorablePerc);
    const colors = getFavorabilityColor(favorablePerc);
    const t = {
        ...item,
        backgroundColor: colors[0],
        color: colors[1],
        favorablePerc,
        theme: item.theme || "",
        avgScore: item.avgScore || null,
        attribute: item.attribute?.value || "",
    };
    return t;
}

export const addMatrixComparisonYAxis = <T>(
    data: Readonly<Array<T>> | undefined,
    comparisonLabel: string,
): Array<T & { yAxis: FacetResult; isComparison: boolean }> =>
    (data || []).map((d) => ({
        ...d,
        yAxis: { value: comparisonLabel, type: "ATTRIBUTE", attribute: "" },
        isComparison: true,
    }));

export const formatMatrixItem = (item: PartialRelayMatrixItem): MatrixItem => ({
    ...item,
    value: item.value ?? null,
    color: "",
    backgroundColor: "",
    displayValue: formatPerc(item.value ?? null),
    totalSample: item.totalSample,
    xAxis: item.xAxis?.value || "",
    yAxis: item.yAxis?.value || "",
    isComparison: item.isComparison || false,
});

export function formatFavorabilityMatrixItem(item: RelayFavorabilityMatrixItem): MatrixItem {
    const value = wholePercentNum(item.value);
    const colors = getFavorabilityColor(value);
    const valueClass = getFavorabilityValueClass(value);
    return {
        ...formatMatrixItem({ ...item, value }),
        backgroundColor: colors[0],
        color: colors[1],
        valueClass,
        avgScore: item.avgScore || null,
    };
}

export function formatResponsivenessMatrixItem(item: RelayResponsivenessMatrixItem): MatrixItem {
    const colors = getResponsivenessColor(item.value ?? null);
    const valueClass = getResponsivenessValueClass(item.value ?? null);
    return {
        ...formatMatrixItem(item),
        displayValue: formatPerc(
            item.value != null ? Math.floor(item.value) : (item.value ?? null),
        ),
        backgroundColor: colors[0],
        color: colors[1],
        valueClass,
    };
}

export function formatEnpsMatrixItem(item: RelayEnpsMatrixItem): MatrixItem {
    const colors = getEnpsColor(item.value ?? null);
    const valueClass = getEnpsValueClass(item.value ?? null);
    return {
        ...formatMatrixItem(item),
        backgroundColor: colors[0],
        color: colors[1],
        displayValue: item.value != null ? String(Math.floor(item.value)) : "",
        valueClass,
    };
}

export function formatThemeChartItem(item: RelayFavorabilityItem): BarChartItemData {
    const fillValue = wholePercentNum(item.favorablePerc);
    const color = getFavorabilityBarChartColor(fillValue);
    return {
        label: item.theme || "",
        color,
        showReadout: true,
        fillValue,
        isEmpty: !item.totalSample,
        count: item.totalSample || 0,
        avgScore: item.avgScore || null,
    };
}

export function formatAttributeChartItem(item: RelayFavorabilityNoThemeItem): BarChartItemData {
    const fillValue = wholePercentNum(item.favorablePerc);
    const color = getFavorabilityBarChartColor(fillValue);
    return {
        label: item.attribute?.value || "",
        color,
        showReadout: true,
        fillValue,
        isEmpty: !item.totalSample,
        count: item.totalSample || 0,
    };
}

export function formatCheckinChartItem(item: RelayFavorabilityCheckinItem): BarChartItemData {
    const fillValue = wholePercentNum(item.favorablePerc);
    const color = getFavorabilityBarChartColor(fillValue);
    return {
        label: item.checkin?.displayTitle || "",
        color,
        showReadout: true,
        fillValue,
        isEmpty: !item.totalSample,
        count: item.totalSample || 0,
    };
}

export function formatResponsivenessAttributeChartItem(
    item: ResponsivenessByAttributeItem,
): BarChartItemData {
    const fillValue = wholePercentNum(item.responsiveness, true);
    const color = getFavorabilityBarChartColor(fillValue);
    const completionVal = item.totalSample ? (item.completion || 0) / item.totalSample : 0;
    const extraTooltipVal = Math.floor(completionVal * 100);
    return {
        label: item.attribute?.value || "",
        color,
        showReadout: true,
        fillValue,
        count: item.totalSample || 0,
        extraTooltipVal,
    };
}

const emptyBarChartData: CheckinRechartsBarData = {
    xAxisTick: "empty",
};

export function formatFilterGroupBarChartItem(
    data: Array<FilterGroupChartData>,
    uniqueDayRanges: Array<Array<number>>,
): Array<FilterGroupRechartsBarData> {
    const dataByFilterGroupId: Record<string, FilterGroupChartData> = data.reduce(
        (acc, d) => (d.attribute ? { ...acc, [d.attribute]: d } : {}),
        {},
    );
    const attributes: string[] = Object.keys(dataByFilterGroupId);
    const chartData = attributes
        .map((attribute: string) => {
            const cData = attribute ? dataByFilterGroupId[attribute] : null;
            if (!cData) return emptyBarChartData;

            const result: FilterGroupRechartsBarData = {
                xAxisTick: attribute || "",
            };
            let checkinHasData = false;
            uniqueDayRanges.forEach((dr, idx) => {
                const prefix = `range${idx}`;
                const rData: BarChartRangeData | undefined = cData?.ranges?.find(
                    ({ range }) => range && range[0] === dr[0],
                );
                const rangeHasValue: boolean = !!rData && rData.value !== null;
                checkinHasData = checkinHasData || rangeHasValue;
                // Bar charts render oddly if you use null values, so coercing to undefined here
                result[prefix] = dr;
                result[`${prefix}Value`] = rangeHasValue ? !!rData && rData.value : undefined;
                result[`${prefix}SampleSize`] = rangeHasValue
                    ? !!rData && rData.sampleSize
                    : undefined;
            });
            if (!checkinHasData) return emptyBarChartData;
            return result;
        })
        .filter((cd) => !!cd && cd !== emptyBarChartData);
    if (!chartData) {
        return [emptyBarChartData];
    }
    return chartData;
}

export function formatCheckinBarChartItem(
    data: Array<CheckinChartData>,
    uniqueDayRanges: Array<Array<number>>,
): Array<CheckinRechartsBarData> {
    const chartData = data
        .map((cData) => {
            if (!cData) return emptyBarChartData;

            const result: CheckinRechartsBarData = {
                xAxisTick: cData.checkin.displayTitle || "",
            };
            let checkinHasData = false;
            uniqueDayRanges.forEach((dr, idx) => {
                const prefix = `range${idx}`;
                const rData: BarChartRangeData | undefined = cData?.ranges?.find(
                    ({ range }) => range && range[0] === dr[0],
                );
                const rangeHasValue: boolean = !!rData && rData.value !== null;
                checkinHasData = checkinHasData || rangeHasValue;
                // Bar charts render oddly if you use null values, so coercing to undefined here
                result[prefix] = dr;
                result[`${prefix}Value`] = rangeHasValue ? !!rData && rData.value : undefined;
                result[`${prefix}SampleSize`] = rangeHasValue
                    ? !!rData && rData.sampleSize
                    : undefined;
            });
            if (!checkinHasData) return emptyBarChartData;
            return result;
        })
        .filter((cd) => !!cd && cd !== emptyBarChartData);
    if (!chartData) {
        return [emptyBarChartData];
    }
    return chartData;
}
export function newlineToHtml(str: string) {
    // convert old-style strings that have newlines to <div>string</div>
    // only if it isn't already html
    // We have to have a parser on the backend to make this safe, because we
    // can't forward along a malicious <script> or <img> tag from a user-generated
    // message, and we can't securely pull those out here.
    if (str.includes("<div>")) {
        return str;
    }
    return str
        .split("\n")
        .map((line) => `<div>${line}</div>`)
        .join("");
}

export function compactNumber(val: number): string {
    const formatter = Intl.NumberFormat("en", { notation: "compact" });
    return formatter.format(val);
}
